import { useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { CostSummaryDto } from '@app/shared';
import { PageHeader } from '../../components/PageHeader';
import { Card, Spinner } from '../../components/ui';
import { useT } from '../../i18n';
import { api } from '../../lib/api';
import { cn } from '../../lib/cn';
import { errorText } from '../../lib/errors';
import { formatCents, personLabel } from '../../lib/format';

const PERIODS = [7, 30, 90] as const;

function Section({ title, children }: { title?: string; children: ReactNode }) {
  return (
    <section className="mb-4">
      {title && <h2 className="text-xs font-semibold uppercase tracking-wider text-hint mb-2">{title}</h2>}
      <Card variant="surface" padding="md">
        {children}
      </Card>
    </section>
  );
}

function Row({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="flex items-baseline justify-between gap-3 py-1.5 text-sm">
      <span className="min-w-0 break-all">
        {label}
        {hint && <span className="block text-xs text-hint">{hint}</span>}
      </span>
      <span className="tabular-nums font-medium whitespace-nowrap">{value}</span>
    </div>
  );
}

/** «dd.mm» of an Almaty day 'YYYY-MM-DD'. */
const shortDay = (day: string) => `${day.slice(8, 10)}.${day.slice(5, 7)}`;

/** What the brain costs (spec 16): totals, the two averages, by step, model, day and person. */
export function AdminCostsPage() {
  const t = useT();
  const [days, setDays] = useState<number>(7);
  const { data, isLoading, error } = useQuery<CostSummaryDto>({
    queryKey: ['admin', 'costs', days],
    queryFn: () => api.adminCosts(days),
  });
  const kinds = t.adminCosts.kinds as Record<string, string>;
  const maxDay = Math.max(0, ...(data?.byDay.map((d) => d.usd) ?? []));

  return (
    <div className="min-h-screen p-4 max-w-lg mx-auto pb-12 safe-top safe-bottom">
      <PageHeader title={t.adminCosts.title} to="/admin" />

      <div className="flex gap-1 p-1 rounded-xl bg-surface-2 mb-4">
        {PERIODS.map((n) => (
          <button
            key={n}
            type="button"
            onClick={() => setDays(n)}
            className={cn(
              'flex-1 h-9 rounded-lg text-sm font-medium transition-colors',
              days === n ? 'bg-surface text-fg shadow-card' : 'text-hint',
            )}
          >
            {t.adminCosts.days(n)}
          </button>
        ))}
      </div>

      {isLoading && (
        <div className="flex justify-center py-6">
          <Spinner />
        </div>
      )}
      {error && <p className="text-sm text-hint">{errorText(error, t)}</p>}

      {data && (
        <>
          <Card variant="surface" padding="md" className="mb-4">
            <p className="text-xs uppercase tracking-wider text-hint">{t.adminCosts.total}</p>
            <p className="text-2xl font-bold tabular-nums">{formatCents(data.totalUsd)}</p>
            <p className="text-xs text-hint">{t.adminCosts.calls(data.calls)}</p>
          </Card>

          {data.calls === 0 ? (
            <p className="text-sm text-hint">{t.adminCosts.empty}</p>
          ) : (
            <>
              <Section>
                <Row
                  label={t.adminCosts.perThought}
                  hint={t.adminCosts.count(data.thought.count)}
                  value={formatCents(data.thought.avgUsd)}
                />
                <Row
                  label={t.adminCosts.perDraft}
                  hint={t.adminCosts.count(data.draft.count)}
                  value={formatCents(data.draft.avgUsd)}
                />
                <Row label={t.adminCosts.eval} value={formatCents(data.evalUsd)} />
              </Section>

              <Section title={t.adminCosts.byKind}>
                {data.byKind.map((k) => (
                  <Row
                    key={k.kind}
                    label={kinds[k.kind] ?? k.kind}
                    hint={[t.adminCosts.calls(k.calls), k.failed ? t.adminCosts.failed(k.failed) : null]
                      .filter(Boolean)
                      .join(' · ')}
                    value={formatCents(k.usd)}
                  />
                ))}
              </Section>

              <Section title={t.adminCosts.byModel}>
                {data.byModel.map((m) => (
                  <Row key={m.model} label={m.model} hint={t.adminCosts.calls(m.calls)} value={formatCents(m.usd)} />
                ))}
              </Section>

              <Section title={t.adminCosts.byDay}>
                <div className="flex items-end gap-px h-24">
                  {data.byDay.map((d) => (
                    <div
                      key={d.day}
                      title={`${shortDay(d.day)}: ${formatCents(d.usd)}`}
                      className="flex-1 min-w-0 rounded-t bg-ink"
                      style={{ height: `${maxDay > 0 ? Math.max(2, (d.usd / maxDay) * 100) : 2}%` }}
                    />
                  ))}
                </div>
                {data.byDay.length > 0 && (
                  <div className="flex justify-between text-xs text-hint mt-1 tabular-nums">
                    <span>{shortDay(data.byDay[0]!.day)}</span>
                    <span>{formatCents(maxDay)}</span>
                    <span>{shortDay(data.byDay[data.byDay.length - 1]!.day)}</span>
                  </div>
                )}
              </Section>

              {data.topUsers.length > 0 && (
                <Section title={t.adminCosts.topUsers}>
                  {data.topUsers.map((u) => (
                    <Row key={u.userId} label={personLabel(u)} value={formatCents(u.usd)} />
                  ))}
                </Section>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}
